/**
 * Public Profile Page — Interactive Two-Panel Layout
 *
 * Desktop: Narrative/Profile + Chat side-by-side in a CSS Grid.
 * Mobile: Tab toggle between Narrative/Profile and Chat views.
 *
 * Shows the Professional Narrative as the primary view when available,
 * with a minimal profile fallback when narrative is missing.
 * The chat agent can focus work history sections via [FOCUS:company] tags.
 */

import { useEffect, useState, useRef, useCallback } from 'preact/hooks';
import { NarrativeView, ProfessionalNarrative } from '../components/NarrativeView';
import { ChatPanel } from '../components/ChatPanel';
import { SkeletonStyles } from '../components/Skeleton';
import { fetchPublicNarrative } from '../api/profile';
import { companyToSectionId } from '../utils/section-ids';
import type { PublicProfileData, ProfileLinks } from '../types/profile';

const API_BASE = import.meta.env.VITE_API_URL || 'https://pxtxt.itsmin.workers.dev';

interface Props {
	tenant: string;
}

type MobileTab = 'profile' | 'chat';

function ProfileSkeleton() {
	return (
		<div class="profile-skeleton">
			<div class="profile-skeleton-header">
				<div class="skeleton skeleton-title" style={{ width: '60%', height: '2.5rem' }} />
				<div class="skeleton skeleton-subtitle" style={{ width: '40%', height: '1.5rem', marginTop: '0.75rem' }} />
				<div class="skeleton skeleton-tagline" style={{ width: '70%', height: '1rem', marginTop: '0.75rem' }} />
			</div>
			<div class="profile-skeleton-section">
				<div class="skeleton skeleton-section-title" style={{ width: '10rem', height: '1.25rem' }} />
				<div class="profile-skeleton-claims">
					{[1, 2, 3].map(i => (
						<div key={i} class="skeleton skeleton-claim" style={{ height: '5rem' }} />
					))}
				</div>
			</div>

			<style>{`
				.profile-skeleton {
					background: var(--color-bg-elevated);
					border: 1px solid var(--color-border);
					overflow: hidden;
				}
				.profile-skeleton-header {
					padding: 3rem 2rem;
					border-bottom: 1px solid var(--color-border);
					display: flex;
					flex-direction: column;
				}
				.profile-skeleton-section { padding: 2rem; }
				.profile-skeleton-claims {
					display: flex;
					flex-direction: column;
					gap: 0.75rem;
				}
				.skeleton-claim { border-radius: var(--radius-md); }
			`}</style>
		</div>
	);
}

/**
 * Minimal profile view when narrative is not available.
 * Shows identity and work history from the profile endpoint.
 */
function MinimalProfileView({ profile, profileLinks }: { profile: PublicProfileData; profileLinks?: ProfileLinks }) {
	const displayName = profile.displayName;
	const title = profile.identity?.positioning?.title;
	const tagline = profile.identity?.positioning?.tagline;
	const focusAreas = profile.identity?.positioning?.focus_areas || [];
	const workHistory = profile.workHistory || [];

	return (
		<div class="minimal-profile">
			<header class="minimal-profile-header">
				<h1 class="minimal-profile-name">{displayName}</h1>
				{title && <p class="minimal-profile-title">{title}</p>}
				{tagline && <p class="minimal-profile-tagline">{tagline}</p>}
				{focusAreas.length > 0 && (
					<div class="minimal-profile-focus">
						{focusAreas.map(area => (
							<span key={area} class="minimal-profile-tag">{area}</span>
						))}
					</div>
				)}
				{profileLinks && (profileLinks.resumeUrl || profileLinks.linkedinUrl || profileLinks.portfolioUrl || profileLinks.email) && (
					<div class="minimal-profile-links">
						{profileLinks.resumeUrl && <a href={profileLinks.resumeUrl} target="_blank" rel="noopener noreferrer" class="minimal-link">Resume</a>}
						{profileLinks.linkedinUrl && <a href={profileLinks.linkedinUrl} target="_blank" rel="noopener noreferrer" class="minimal-link">LinkedIn</a>}
						{profileLinks.portfolioUrl && <a href={profileLinks.portfolioUrl} target="_blank" rel="noopener noreferrer" class="minimal-link">Portfolio</a>}
						{profileLinks.email && <a href={`mailto:${profileLinks.email}`} class="minimal-link">Contact</a>}
					</div>
				)}
			</header>

			{workHistory.length > 0 && (
				<section class="minimal-profile-experience">
					<h2 class="minimal-section-heading">Experience</h2>
					{workHistory.map(job => (
						<div key={job.company} id={companyToSectionId(job.company)} class="minimal-job">
							<div class="minimal-job-company">{job.company}</div>
							{job.title && <div class="minimal-job-title">{job.title}</div>}
							{job.period && <div class="minimal-job-period">{job.period}</div>}
						</div>
					))}
				</section>
			)}

			<style>{minimalProfileStyles}</style>
		</div>
	);
}

export function PublicProfile({ tenant }: Props) {
	const [profile, setProfile] = useState<PublicProfileData | null>(null);
	const [narrative, setNarrative] = useState<ProfessionalNarrative | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Focus state
	const [focusedCompany, setFocusedCompany] = useState<string | null>(null);
	const focusTimeoutRef = useRef<number | null>(null);
	const profilePanelRef = useRef<HTMLDivElement>(null);

	// User scroll guard: suppress agent-initiated scroll if user scrolled recently
	const lastUserScrollRef = useRef<number>(0);

	// Mobile tab
	const [mobileTab, setMobileTab] = useState<MobileTab>('profile');
	const [focusToast, setFocusToast] = useState<string | null>(null);
	const toastTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		loadProfile();
	}, [tenant]);

	// Track user scroll on profile panel
	useEffect(() => {
		const panel = profilePanelRef.current;
		if (!panel) return;

		function onScroll() {
			lastUserScrollRef.current = Date.now();
		}

		panel.addEventListener('scroll', onScroll, { passive: true });
		return () => panel.removeEventListener('scroll', onScroll);
	}, [loading]);

	async function loadProfile() {
		try {
			setLoading(true);
			setError(null);

			// Load profile and narrative in parallel
			const [profileRes, narrativeData] = await Promise.all([
				fetch(`${API_BASE}/api/public/profile/${tenant}`),
				fetchPublicNarrative(tenant),
			]);

			if (!profileRes.ok) {
				setError(profileRes.status === 404 ? 'Profile not found' : 'Failed to load profile');
				return;
			}
			setProfile(await profileRes.json());

			if (narrativeData) {
				setNarrative(narrativeData as ProfessionalNarrative);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load profile');
		} finally {
			setLoading(false);
		}
	}

	/**
	 * Match a focus company name from the agent against work history entries.
	 * Checks both profile work history and narrative role sections.
	 */
	function matchCompany(focusName: string): string | null {
		const companies: string[] = [];

		// From profile work history
		if (profile?.workHistory) {
			companies.push(...profile.workHistory.map(e => e.company));
		}

		// From narrative role sections
		if (narrative?.sections) {
			for (const s of narrative.sections) {
				if (s.type === 'role' && s.company) {
					companies.push(s.company);
				}
			}
		}

		if (companies.length === 0) return null;

		// Exact match
		const exact = companies.find(c => c === focusName);
		if (exact) return exact;

		// Case-insensitive
		const lower = focusName.toLowerCase();
		const ciMatch = companies.find(c => c.toLowerCase() === lower);
		if (ciMatch) return ciMatch;

		// Partial/contains
		const partial = companies.find(
			c => c.toLowerCase().includes(lower) || lower.includes(c.toLowerCase())
		);
		if (partial) return partial;

		return null;
	}

	// Debounced focus handler
	const focusDebounceRef = useRef<number | null>(null);

	const handleFocus = useCallback((companyName: string) => {
		if (focusDebounceRef.current) {
			clearTimeout(focusDebounceRef.current);
		}

		focusDebounceRef.current = window.setTimeout(() => {
			const matched = matchCompany(companyName);
			if (!matched) return;

			// Clear previous focus timeout
			if (focusTimeoutRef.current) {
				clearTimeout(focusTimeoutRef.current);
			}

			setFocusedCompany(matched);

			// Check if mobile and on chat tab — show toast instead
			const isMobile = window.innerWidth < 1024;
			if (isMobile && mobileTab === 'chat') {
				setFocusToast(matched);
				if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
				toastTimeoutRef.current = window.setTimeout(() => setFocusToast(null), 5000);
			} else {
				// Desktop or profile tab: scroll if user hasn't scrolled recently
				const timeSinceUserScroll = Date.now() - lastUserScrollRef.current;
				if (timeSinceUserScroll > 5000) {
					const sectionId = companyToSectionId(matched);
					const el = document.getElementById(sectionId);
					if (el) {
						el.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				}
			}

			// Clear focus highlight after 3s
			focusTimeoutRef.current = window.setTimeout(() => {
				setFocusedCompany(null);
			}, 3000);
		}, 500);
	}, [profile, narrative, mobileTab]);

	function handleToastView() {
		setMobileTab('profile');
		setFocusToast(null);
		requestAnimationFrame(() => {
			if (focusedCompany) {
				const el = document.getElementById(companyToSectionId(focusedCompany));
				if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		});
	}

	// Loading state
	if (loading) {
		return (
			<div class="page">
				<header class="pb-header">
					<div class="pb-header-inner">
						<a href="/" class="logo-link"><span class="logo">pxtxt</span></a>
					</div>
				</header>
				<main class="pb-loading">
					<SkeletonStyles />
					<ProfileSkeleton />
				</main>
				<style>{publicProfileStyles}</style>
			</div>
		);
	}

	// Error state
	if (error || !profile) {
		return (
			<div class="page">
				<header class="pb-header">
					<div class="pb-header-inner">
						<a href="/" class="logo-link"><span class="logo">pxtxt</span></a>
					</div>
				</header>
				<main class="pb-loading">
					<div class="profile-error">
						<div class="error-icon">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
								<circle cx="12" cy="12" r="10" />
								<path d="M15 9l-6 6M9 9l6 6" />
							</svg>
						</div>
						<h2 class="t-title">Profile Not Found</h2>
						<p class="t-body text-muted">
							The professional profile for "{tenant}" doesn't exist or isn't public.
						</p>
						<a href="/" class="btn btn-primary">Learn about pxtxt</a>
					</div>
				</main>
				<style>{publicProfileStyles}</style>
			</div>
		);
	}

	const chatEnabled = profile.chatEnabled ?? false;
	const firstName = profile.displayName?.split(' ')[0] || '';

	// Build profile links
	const profileLinks: ProfileLinks | undefined = profile ? {
		resumeUrl: profile.resumeUrl,
		linkedinUrl: profile.linkedinUrl,
		portfolioUrl: profile.portfolioUrl,
		email: profile.identity?.contact?.email,
	} : undefined;

	// Identity data from profile
	const displayName = profile.displayName;
	const profileTitle = profile.identity?.positioning?.title;
	const profileTagline = profile.identity?.positioning?.tagline;
	const profileFocusAreas = profile.identity?.positioning?.focus_areas;

	return (
		<div class="page pb-page">
			{/* Sticky header */}
			<header class="pb-header">
				<div class="pb-header-inner">
					<a href="/" class="logo-link"><span class="logo">pxtxt</span></a>
					<a href="/" class="btn btn-secondary btn-sm">Get yours</a>
				</div>
			</header>

			{/* Mobile tab bar */}
			{chatEnabled && (
				<div class="pb-tabs" role="tablist">
					<button
						class={`pb-tab ${mobileTab === 'profile' ? 'pb-tab--active' : ''}`}
						onClick={() => setMobileTab('profile')}
						role="tab"
						aria-selected={mobileTab === 'profile'}
						aria-controls="pb-profile-panel"
					>
						{narrative ? 'Narrative' : 'Profile'}
					</button>
					<button
						class={`pb-tab ${mobileTab === 'chat' ? 'pb-tab--active' : ''}`}
						onClick={() => setMobileTab('chat')}
						role="tab"
						aria-selected={mobileTab === 'chat'}
						aria-controls="pb-chat-panel"
					>
						Chat
					</button>
				</div>
			)}

			{/* Two-panel grid */}
			<div class={`pb-grid ${chatEnabled ? '' : 'pb-grid--single'}`}>
				{/* Profile panel — narrative (primary) or minimal profile (fallback) */}
				<div
					ref={profilePanelRef}
					id="pb-profile-panel"
					role="tabpanel"
					class={`pb-profile-panel ${chatEnabled && mobileTab !== 'profile' ? 'pb-panel--hidden' : ''}`}
				>
					{narrative ? (
						<NarrativeView
							narrative={narrative}
							name={displayName}
							title={profileTitle}
							tagline={profileTagline}
							focusAreas={profileFocusAreas}
							isPublic={true}
							slug={tenant}
							focusedCompany={focusedCompany}
							profileLinks={profileLinks}
							betaEntities={profile.betaEntities}
						/>
					) : (
						<MinimalProfileView
							profile={profile}
							profileLinks={profileLinks}
						/>
					)}
				</div>

				{/* Chat panel */}
				{chatEnabled && (
					<div id="pb-chat-panel" role="tabpanel" class={`pb-chat-panel ${mobileTab !== 'chat' ? 'pb-panel--hidden' : ''}`}>
						<ChatPanel
							slug={tenant}
							displayName={profile.displayName || firstName}
							profileContext={{
								workHistory: profile.workHistory,
								focusAreas: profile.identity?.positioning?.focus_areas,
							}}
							onFocus={handleFocus}
							embedded={true}
						/>
					</div>
				)}
			</div>

			{/* Mobile focus toast */}
			{focusToast && (
				<div class="pb-focus-toast">
					<span>Discussing: {focusToast}</span>
					<button class="pb-toast-view" onClick={handleToastView}>View</button>
				</div>
			)}

			<style>{publicProfileStyles}</style>
		</div>
	);
}

const minimalProfileStyles = `
	.minimal-profile {
		max-width: 700px;
		margin: 0 auto;
	}

	.minimal-profile-header {
		padding: 2.5rem 0 2rem;
		border-bottom: 1px solid var(--color-border);
		margin-bottom: 2rem;
	}

	.minimal-profile-name {
		font-family: var(--font-display);
		font-size: 2rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0;
	}

	.minimal-profile-title {
		font-size: 1.125rem;
		color: var(--color-text);
		margin: 0.375rem 0 0;
	}

	.minimal-profile-tagline {
		font-size: 0.9375rem;
		color: var(--color-text-muted);
		margin: 0.5rem 0 0;
		line-height: 1.5;
	}

	.minimal-profile-focus {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin-top: 1rem;
	}

	.minimal-profile-tag {
		padding: 0.25rem 0.75rem;
		background: var(--color-bg-hover);
		border-radius: var(--radius-md);
		font-family: var(--font-mono);
		font-size: 0.75rem;
		color: var(--color-text-muted);
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.minimal-profile-links {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 1.25rem;
	}

	.minimal-link {
		font-size: 0.875rem;
		color: var(--color-primary);
		text-decoration: none;
	}

	.minimal-link:hover {
		text-decoration: underline;
	}

	.minimal-section-heading {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--color-text-muted);
		margin: 0 0 1.25rem;
	}

	.minimal-job {
		padding: 1rem 0;
		border-bottom: 1px solid var(--color-border);
	}

	.minimal-job:last-child {
		border-bottom: none;
	}

	.minimal-job-company {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 400;
		color: var(--color-text);
	}

	.minimal-job-title {
		font-size: 0.9375rem;
		color: var(--color-text-muted);
		margin-top: 0.25rem;
	}

	.minimal-job-period {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		color: var(--color-text-muted);
		margin-top: 0.25rem;
	}
`;

const publicProfileStyles = `
	/* -- Page layout -- */

	.pb-page {
		height: 100vh;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	/* -- Header -- */

	.pb-header {
		padding: 0.75rem 1.5rem;
		border-bottom: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		flex-shrink: 0;
		z-index: 100;
	}

	.pb-header-inner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: 1400px;
		margin: 0 auto;
	}

	.logo-link { text-decoration: none; }

	.logo {
		font-family: var(--font-display);
		font-size: 1.5rem;
		font-weight: 700;
		color: var(--color-primary);
	}

	.btn-sm {
		padding: 0.5rem 1rem;
		font-size: 0.875rem;
	}

	/* -- Mobile tabs -- */

	.pb-tabs {
		display: none;
		border-bottom: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		flex-shrink: 0;
	}

	.pb-tab {
		flex: 1;
		padding: 0.625rem 1rem;
		background: none;
		border: none;
		border-bottom: 2px solid transparent;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: color 150ms, border-color 150ms;
	}

	.pb-tab--active {
		color: var(--color-text);
		border-bottom-color: var(--color-primary);
	}

	/* -- Grid -- */

	.pb-grid {
		flex: 1;
		display: grid;
		grid-template-columns: 1fr min(420px, 33vw);
		min-height: 0;
	}

	.pb-grid--single {
		grid-template-columns: 1fr;
	}

	/* -- Profile panel -- */

	.pb-profile-panel {
		overflow-y: auto;
		padding: 2rem;
	}

	.pb-profile-panel .narrative-container {
		max-width: none;
		padding: 0;
	}

	/* -- Chat panel -- */

	.pb-chat-panel {
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	.pb-chat-panel .chat-panel {
		flex: 1;
		min-height: 0;
	}

	/* -- Loading/error states -- */

	.pb-loading {
		flex: 1;
		padding: 2rem;
		display: flex;
		align-items: flex-start;
		justify-content: center;
	}

	.pb-loading .profile-skeleton {
		max-width: 800px;
		width: 100%;
	}

	.profile-error {
		text-align: center;
		padding: 4rem 2rem;
		max-width: 500px;
		margin: 0 auto;
	}

	.error-icon {
		color: var(--color-error);
		margin-bottom: 1.5rem;
	}

	.profile-error .t-title { margin-bottom: 0.75rem; }
	.profile-error .t-body { margin-bottom: 2rem; }

	/* -- Focus toast (mobile) -- */

	.pb-focus-toast {
		position: fixed;
		bottom: 1rem;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding: 0.625rem 1rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: var(--radius-md);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		font-size: 0.8125rem;
		color: var(--color-text);
		z-index: 200;
		animation: toast-in 200ms ease;
	}

	.pb-toast-view {
		padding: 0.25rem 0.625rem;
		background: var(--color-primary);
		border: none;
		border-radius: var(--radius-md);
		color: white;
		font-size: 0.75rem;
		font-weight: 500;
		cursor: pointer;
	}

	@keyframes toast-in {
		from { opacity: 0; transform: translateX(-50%) translateY(8px); }
		to { opacity: 1; transform: translateX(-50%) translateY(0); }
	}

	/* -- Responsive: mobile -- */

	@media (max-width: 1023px) {
		.pb-tabs {
			display: flex;
		}

		.pb-grid {
			grid-template-columns: 1fr;
		}

		.pb-panel--hidden {
			display: none;
		}

		.pb-profile-panel {
			padding: 1rem;
		}

		.pb-chat-panel {
			height: 100%;
		}
	}
`;
